/**
 * Hook de Transacciones
 * Maneja el envío, recepción y sincronización de transacciones
 */

import { useState, useEffect, useCallback } from 'react';
import { useWalletStore } from '@/store/walletStore';
import transactionService from '@/services/blockchain/transaction.service';
import { TransactionSyncService } from '@/services/transactions/transaction-sync.service';
import { Transaction } from '@/types/transaction.types';

interface SendTokenParams {
  to: string;
  amount: string;
  tokenAddress?: string;
  gasLimit?: string;
  gasPrice?: string;
}

export function useTransactions(networkOverride?: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const { selectedNetwork, transactions, setTransactions, address: walletAddress } = useWalletStore();
  // Solo para lectura — las acciones siguen atadas a selectedNetwork real.
  const network = networkOverride || selectedNetwork;

  /**
   * Inicia la sincronización automática cuando hay una wallet activa
   */


  /**
   * Actualiza las transacciones desde el cache
   */
  const refreshTransactions = useCallback(() => {
    if (!walletAddress) return;

    const syncService = TransactionSyncService.getInstance();
    const cachedTransactions = syncService.getTransactions(walletAddress, network);
    setTransactions(cachedTransactions);
  }, [walletAddress, network, setTransactions]);

  /**
   * Inicia la sincronización automática cuando hay una wallet activa
   * y se suscribe al evento 'transactions:synced' para actualizar la UI
   * en cuanto el servicio de sync tenga datos nuevos.
   */
  useEffect(() => {
    if (!walletAddress) return;
    let cancelled = false;

    const syncService = TransactionSyncService.getInstance();

    // Mostrar lo cacheado ya mismo, y loading hasta confirmar que no hay más nuevo.
    refreshTransactions();
    setLoading(true);
    syncService.startAutoSync(walletAddress).then(() => {
      if (cancelled) return;
      refreshTransactions();
      setLoading(false);
    });

    // Filtramos por la red activa para no re-renderizar en cambios de otras redes.
    const handleSynced = (e: Event) => {
      const detail = (e as CustomEvent<{ walletAddress: string; network: string }>).detail;
      if (
        detail.walletAddress.toLowerCase() === walletAddress.toLowerCase() &&
        detail.network === network
      ) {
        refreshTransactions();
      }
    };

    window.addEventListener('transactions:synced', handleSynced);

    return () => {
      cancelled = true;
      syncService.stopAutoSync(walletAddress);
      window.removeEventListener('transactions:synced', handleSynced);
    };
  }, [walletAddress, network, refreshTransactions]);

  /**
   * Envía tokens (detecta automáticamente si es nativo o ERC-20)
   */
  const sendToken = async (params: SendTokenParams) => {
    try {
      setLoading(true);
      setError(null);
      setTxHash(null);

      const result = await transactionService.sendTransaction(params, selectedNetwork);

      setTxHash(result.hash);

      // La transacción ya se guarda automáticamente en el cache
      // Solo necesitamos actualizar el estado local
      refreshTransactions();

      return result;
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Estima el gas para una transacción
   */
  const estimateGas = async (
    from: string,
    to: string,
    amount: string,
    tokenAddress?: string,
    decimals?: number
  ) => {
    try {
      if (!tokenAddress) {
        return await transactionService.estimateNativeGas(from, to, amount, selectedNetwork);
      } else {
        return await transactionService.estimateERC20Gas(
          from,
          to,
          amount,
          tokenAddress,
          decimals || 18,
          selectedNetwork
        );
      }
    } catch (err) {
      console.error('Error estimating gas:', err);
      return BigInt(0);
    }
  };

  /**
   * Fuerza una sincronización de transacciones
   */
  const forceSync = async () => {
    if (!walletAddress) return;

    try {
      setSyncing(true);
      await transactionService.syncTransactions(walletAddress, selectedNetwork);
      refreshTransactions();
    } catch (err) {
      console.error('Error syncing transactions:', err);
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Obtiene el historial de transacciones desde el cache
   */
  const fetchTransactionHistory = async (address: string) => {
    try {
      setLoading(true);
      setError(null);

      const history = transactionService.getTransactionHistory(address, selectedNetwork);
      
      const formattedTransactions: Transaction[] = history.map((tx: any) => ({
        hash: tx.hash,
        from: tx.from,
        to: tx.to,
        value: tx.value?.toString() || '0',
        timestamp: tx.timestamp || Date.now(),
        status: tx.status || 'confirmed',
        type: tx.type || (tx.from.toLowerCase() === address.toLowerCase() ? 'send' : 'receive'),
        blockNumber: tx.blockNumber || 0,
        network: tx.network || selectedNetwork,
        source: tx.source || 'user_sent',
      }));

      setTransactions(formattedTransactions);
      return formattedTransactions;
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      console.error('Error fetching transaction history:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  /**
   * Obtiene los detalles de una transacción
   */
  const getTransaction = async (txHash: string) => {
    try {
      const tx = await transactionService.getTransaction(txHash, selectedNetwork);
      return tx;
    } catch (err) {
      console.error('Error getting transaction:', err);
      return null;
    }
  };

  /**
   * Trae nonce/gasLimit/gasUsed/gasPrice en vivo por RPC para una tx puntual
   * (no vienen en el registro cacheado del historial).
   */
  const getExtendedTransactionDetails = async (
    txHash: string,
    txNetwork: string
  ): Promise<Partial<Transaction>> => {
    try {
      const [tx, receipt] = await Promise.all([
        transactionService.getTransaction(txHash, txNetwork),
        transactionService.getTransactionReceipt(txHash, txNetwork),
      ]);

      return {
        nonce: tx?.nonce,
        gasLimit: tx?.gasLimit?.toString(),
        gasPrice: tx?.gasPrice?.toString(),
        gasUsed: receipt?.gasUsed?.toString(),
      };
    } catch (err) {
      console.error('Error getting extended transaction details:', err);
      return {};
    }
  };

  /**
   * Espera a que una transacción sea confirmada
   */
  const waitForTransaction = async (txHash: string, confirmations = 1) => {
    try {
      setLoading(true);
      const receipt = await transactionService.waitForTransaction(txHash, selectedNetwork, confirmations);
      
      // Actualizar transacciones después de confirmación
      refreshTransactions();
      
      return receipt;
    } catch (err) {
      console.error('Error waiting for transaction:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Obtiene transacciones pendientes
   */
  const getPendingTransactions = (): Transaction[] => {
    if (!walletAddress) return [];
    
    const syncService = TransactionSyncService.getInstance();
    return syncService.getPendingTransactions(walletAddress, selectedNetwork);
  };

  return {
    // Estado
    transactions,
    loading,
    error,
    txHash,
    syncing,

    // Acciones
    sendToken,
    estimateGas,
    fetchTransactionHistory,
    getTransaction,
    getExtendedTransactionDetails,
    waitForTransaction,
    forceSync,
    refreshTransactions,
    getPendingTransactions,
  };
}

export default useTransactions;
